import React, { useCallback, useMemo, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import differenceInDays from 'date-fns/differenceInDays';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

import {
  managementReportRequested,
  managementChangeFilterCountry,
  managementChangeFilterStartDate,
  managementChangeFilterEndDate,
} from '../../state/managementReport/actionCreators';
import {
  AppState,
  activeCountriesDataSelector,
  filterParametersLoadedSelector,
  parametersRequested,
  managementReportMaxDateRangeSelector,
} from '../../state';
import { ReportFilterActions, ReportFilters } from '../ReportFilters';
import { CountrySelect } from '../selects';
import DateRangeFlexSales from '../DateRangeFlexSales';
import { Country, AppPermissions } from '../../types';
import { PermissionsProvider } from '../Permissions';

const resourcePermissions = [AppPermissions.CLOSING_DAYS_REPORT];

const ManagementReportSearch = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isReady, setIsReady] = useState(false);

  const filters = useSelector((state: AppState) => state.managementReport.filters);
  const reportResource = useSelector((state: AppState) => state.managementReport.report);
  const userCountriesData = useSelector(activeCountriesDataSelector) || [];
  const userCountries = useMemo(() => userCountriesData, [userCountriesData]);
  const isParamsLoaded = useSelector(filterParametersLoadedSelector);

  const maxDaysLimit = useSelector(managementReportMaxDateRangeSelector);

  useEffect(() => {
    if (!isParamsLoaded) {
      dispatch(parametersRequested());
    }
  }, [dispatch, isParamsLoaded]);

  useEffect(() => {
    if (!(isParamsLoaded && userCountries.length > 0)) {
      return undefined;
    }
    const timer = setTimeout(() => setIsReady(true), 500);
    return () => clearTimeout(timer);
  }, [isParamsLoaded, userCountries.length]);

  const rangeError = useMemo(() => {
    if (filters.startDate && filters.endDate) {
      const diff = Math.abs(
        differenceInDays(new Date(filters.endDate), new Date(filters.startDate))
      );

      if (diff >= maxDaysLimit) {
        return t('management-report.errors.maxRange', {
          max: maxDaysLimit,
          defaultValue: `El rango máximo permitido es de ${maxDaysLimit} días.`,
        });
      }
    }
    return null;
  }, [filters.startDate, filters.endDate, maxDaysLimit, t]);

  const selectedCountryValue = useMemo(() => {
    if (!filters.countryCode || userCountries.length === 0) return [];
    const match = userCountries.find((c: Country) => c.countryCode === filters.countryCode);
    return match ? [match] : [];
  }, [filters.countryCode, userCountries]);

  const handleCountryChange = useCallback(
    (selectedCountries: Country[]) => {
      const nextCode =
        selectedCountries.length > 0
          ? selectedCountries[selectedCountries.length - 1].countryCode
          : '';

      if (nextCode === filters.countryCode) return;

      dispatch(managementChangeFilterCountry(nextCode));
    },
    [dispatch, filters.countryCode]
  );

  const handleDateChange = useCallback(
    ([start, end]: [Date | null, Date | null]) => {
      dispatch(managementChangeFilterStartDate(start));
      dispatch(managementChangeFilterEndDate(end));
    },
    [dispatch]
  );

  const applyDisabled =
    !filters.countryCode || !filters.startDate || !filters.endDate || !!rangeError;

  if (!isReady) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <ReportFilters
      loadingSelector={() => reportResource.loading}
      applyAction={managementReportRequested}
    >
      <PermissionsProvider resourcePermissions={resourcePermissions}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <Box>
              <CountrySelect
                label={t('toolbar.labels.country')}
                value={selectedCountryValue}
                onChange={handleCountryChange}
                fullWidth
                propId="countryCode"
                translationKey="operativeStructure.countries"
                showCountBadge={false}
                isOptionEqualToValue={(option: Country, val: Country) =>
                  option.countryCode === val.countryCode
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <DateRangeFlexSales
              startText={t('toolbar.labels.fromDate')}
              endText={t('toDate')}
              value={[filters.startDate, filters.endDate]}
              onChange={handleDateChange}
            />
          </Grid>
          <Grid item xs={12} md lg xl sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ReportFilterActions applyDisabled={applyDisabled} />
          </Grid>
          {rangeError && (
            <Grid item xs={12}>
              <Alert severity="warning" variant="outlined" sx={{ border: 'none', py: 0 }}>
                {rangeError}
              </Alert>
            </Grid>
          )}
        </Grid>
      </PermissionsProvider>
    </ReportFilters>
  );
};

export default ManagementReportSearch;
